import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import isObject from 'lodash/isObject';

type RichTextResult =
  { type: 'text' | 'hyperlink'; value: string } | { type: 'entry-hyperlink'; entryId?: string };

export const getEntryId = (target: unknown): string | undefined => {
  const sys = isObject(target) && 'sys' in target ? target.sys : undefined;
  return isObject(sys) && 'id' in sys && typeof sys.id === 'string' ? sys.id : undefined;
};

export const isEntryHyperlink = (
  node: unknown
): node is {
  nodeType: INLINES.ENTRY_HYPERLINK;
  data: { target?: unknown };
  content: unknown[];
} =>
  isObject(node) &&
  'nodeType' in node &&
  node.nodeType === INLINES.ENTRY_HYPERLINK &&
  'data' in node &&
  isObject(node.data) &&
  'content' in node &&
  Array.isArray(node.content);

const isHyperlinkNode = (
  node: unknown
): node is { nodeType: INLINES.HYPERLINK; data: { uri: string } } =>
  isObject(node) &&
  'nodeType' in node &&
  node.nodeType === INLINES.HYPERLINK &&
  'data' in node &&
  isObject(node.data) &&
  'uri' in node.data &&
  typeof node.data.uri === 'string';

export const resolveRichTextNode = (node: unknown): RichTextResult => {
  if (!isObject(node) || !('nodeType' in node)) {
    return { type: 'text', value: '' };
  }

  if (node.nodeType === INLINES.ENTRY_HYPERLINK) {
    const target =
      'data' in node && isObject(node.data) && 'target' in node.data ? node.data.target : undefined;
    return { type: 'entry-hyperlink', entryId: getEntryId(target) };
  }

  if (isHyperlinkNode(node)) {
    return { type: 'hyperlink', value: node.data.uri };
  }

  if (node.nodeType === 'text' && 'value' in node && typeof node.value === 'string') {
    return { type: 'text', value: node.value };
  }

  const text: string[] = [];

  // recursive loop to go through the nodes to reach the handle
  if ('content' in node && Array.isArray(node.content)) {
    for (const child of node.content) {
      const result = resolveRichTextNode(child);

      if (result.type !== 'text') {
        return result;
      }

      text.push(result.value);
    }
  }

  // to save line breaks between paragraphs
  return { type: 'text', value: text.join(node.nodeType === BLOCKS.DOCUMENT ? '\n\n' : '') };
};
